import React from "react";
import {
  Organization,
  BackArrowLeft,
  Product,
  UserManagement,
  Diamond,
  Account,
  People,
  Magnet,
  Connections,
  Contracts,
  Partners,
  Integrations,
  NoteTaker
} from "@dreamhub/designsystem/icons";
import { KeyRound } from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import { useNavigate } from "react-router-dom";
import { Nav } from "@/pages/settings/components/settingsNav/Nav";
import { useTranslation } from "react-i18next";
import { SETTINGS_REFERRER_KEY } from "@/constants";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";
import { useIsSandboxAccount } from "@/services/auth/useIsSandboxAccount";
import { useIsNoteTakerAvailable } from "@/services/auth/useIsNoteTakerAvailable";

interface SettingsSideBarProps {
  currentPageName?: string;
  onPageClick: (targetName: string) => void;
}

const TRANSLATION_KEY = "settings.navigation";

const SettingsSideBar: React.FC<SettingsSideBarProps> = ({
  currentPageName,
  onPageClick
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { enabled: showPartnersSettings } = useFeatureFlag(
    FeatureFlags.CRM_7805_PARTNERSHIPS
  );
  const { enabled: showIntegrationsSettings } = useFeatureFlag(
    FeatureFlags.CRM_9459_INTEGRATIONS_SETTINGS
  );
  const canAccessAdminSettings = useCanAccessAdminSettings();
  const isSandbox = useIsSandboxAccount();
  const isNoteTakerAvailable = useIsNoteTakerAvailable();

  const handleBackClick = () => {
    const referrer = sessionStorage.getItem(SETTINGS_REFERRER_KEY);
    if (referrer && referrer !== "/settings") {
      navigate(referrer);
    } else {
      navigate("/");
    }
  };

  return (
    <aside
      className={cn(
        "bg-surface-sidebar-navigation text-h1 flex flex-col h-full overflow-x-hidden overflow-y-auto sticky top-0 pt-4 left-0",
        "transition-all duration-300 ease-in-out",
        "w-52 max-w-52 min-w-52"
      )}
    >
      <div className="flex-1">
        <div className="px-3">
          <Button
            variant="secondaryNoBg"
            color="h1"
            className="p-[6px] pr-3 gap-[6px] border-transparent"
            onClick={handleBackClick}
          >
            <IconContainer color="h1">
              <BackArrowLeft />
            </IconContainer>
            {t(`${TRANSLATION_KEY}.back`)}
          </Button>
        </div>
        <Nav
          currentPageName={currentPageName}
          isCollapsed={false}
          onPageClick={onPageClick}
          sections={[
            {
              title: t(`${TRANSLATION_KEY}.myAccount`),
              items: [
                // A sandbox can't connect email or calendar accounts.
                ...(isSandbox
                  ? []
                  : [
                      {
                        title: t(`${TRANSLATION_KEY}.connectedAccounts`),
                        icon: Connections,
                        variant: "default" as const,
                        target: "connectedAccounts"
                      }
                    ]),
                {
                  title: t(`${TRANSLATION_KEY}.apiKeys`),
                  icon: KeyRound,
                  variant: "default" as const,
                  target: "apiKeys"
                }
              ]
            },
            ...(canAccessAdminSettings
              ? [
                  {
                    title: t(`${TRANSLATION_KEY}.admin`),
                    items: [
                      {
                        title: t(`${TRANSLATION_KEY}.general`),
                        icon: Organization,
                        variant: "default" as const,
                        target: "general"
                      },
                      {
                        title: t(`${TRANSLATION_KEY}.userManagement`),
                        icon: UserManagement,
                        variant: "default" as const,
                        target: "userManagement"
                      },
                      ...(showIntegrationsSettings
                        ? [
                            {
                              title: t(`${TRANSLATION_KEY}.integrations`),
                              icon: Integrations,
                              variant: "default" as const,
                              target: "integrations"
                            },
                            ...(isNoteTakerAvailable
                              ? [
                                  {
                                    title: t(`${TRANSLATION_KEY}.noteTaker`),
                                    icon: NoteTaker,
                                    variant: "default" as const,
                                    target: "noteTaker"
                                  }
                                ]
                              : [])
                          ]
                        : [])
                    ]
                  },
                  {
                    title: t(`${TRANSLATION_KEY}.workspace`),
                    items: [
                      {
                        title: t(`${TRANSLATION_KEY}.productsLicenses`),
                        icon: Product,
                        variant: "default" as const,
                        target: "productsLicenses"
                      }
                    ]
                  },
                  {
                    title: t(`${TRANSLATION_KEY}.dreamhubsRecords`),
                    items: [
                      {
                        title: t(`${TRANSLATION_KEY}.companies`),
                        icon: Account,
                        variant: "default" as const,
                        target: "companies"
                      },
                      {
                        title: t(`${TRANSLATION_KEY}.people`),
                        icon: People,
                        variant: "default" as const,
                        target: "people"
                      },
                      ...(showPartnersSettings
                        ? [
                            {
                              title: t(`${TRANSLATION_KEY}.partners`),
                              icon: Partners,
                              variant: "default" as const,
                              target: "partners"
                            }
                          ]
                        : []),
                      {
                        title: t(`${TRANSLATION_KEY}.leads`),
                        icon: Magnet,
                        variant: "default" as const,
                        target: "leads"
                      },
                      {
                        title: t(`${TRANSLATION_KEY}.deals`),
                        icon: Diamond,
                        variant: "default" as const,
                        target: "deals"
                      },
                      {
                        title: t(`${TRANSLATION_KEY}.contracts`),
                        icon: Contracts,
                        variant: "default" as const,
                        target: "contracts"
                      }
                    ]
                  }
                ]
              : [])
          ]}
        />
      </div>
      <div
        className={cn(
          "fixed bottom-0 flex flex-row items-end justify-end p-4",
          "w-52 max-w-52 min-w-52"
        )}
      ></div>
    </aside>
  );
};

export default SettingsSideBar;
